/**
 * PATH: packages/email-hub/src/feature/email-layout/EmailSidebarFooter.tsx
 * FILE_ID: EMAIL-LAYOUT-SIDEBAR-FOOTER-001
 * PURPOSE: Keep capacity, issues, collapse, and appearance controls pinned.
 */

import {
  AlertCircle,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react'
import { cn } from '../../_internal/lib/utils'
import { EmailBackgroundControl } from '../components/EmailBackgroundControl'
import { LayoutControl } from './LayoutControl'
import type { EmailLayoutController } from './useEmailLayoutController'

export function EmailSidebarFooter({
  layout,
}: {
  layout: EmailLayoutController
}) {
  const issueCount =
    layout.issueErrorCount > 0
      ? layout.issueErrorCount
      : layout.issueWarningCount

  return (
    <>
      <div
        className={cn(
          'border-t border-border',
          layout.collapsed ? 'p-1.5' : 'px-3 py-2',
        )}
      >
        <button
          type="button"
          onClick={() => layout.setIssuesOpen(true)}
          className={cn(
            'flex items-center rounded-lg transition-colors relative',
            layout.issueErrorCount > 0
              ? 'text-red-500 hover:bg-red-500/10'
              : layout.issueWarningCount > 0
                ? 'text-amber-500 hover:bg-amber-500/10'
                : 'text-muted-foreground hover:bg-accent hover:text-foreground',
            layout.collapsed
              ? 'justify-center h-8 w-8 mx-auto'
              : 'gap-2 w-full px-2 py-1.5 text-xs',
          )}
          title={
            layout.issueErrorCount > 0
              ? `${layout.issueErrorCount} recent error${layout.issueErrorCount === 1 ? '' : 's'}`
              : layout.issueWarningCount > 0
                ? `${layout.issueWarningCount} recent warning${layout.issueWarningCount === 1 ? '' : 's'}`
                : 'Recent issues'
          }
          aria-label="Recent issues"
        >
          <AlertCircle className="w-3.5 h-3.5" />
          {!layout.collapsed && <span>Recent issues</span>}
          {issueCount > 0 && (
            <span
              className={cn(
                'ml-auto text-[10px] font-semibold rounded-full px-1.5 py-0.5 leading-none',
                layout.issueErrorCount > 0
                  ? 'bg-red-500 text-white'
                  : 'bg-amber-500 text-black',
                layout.collapsed && 'absolute top-0 right-0 ml-0',
              )}
            >
              {issueCount}
            </span>
          )}
        </button>
      </div>
      {!layout.isNarrow && (
        <div
          className={cn(
            'border-t border-border flex items-center gap-1',
            layout.collapsed ? 'p-1.5 flex-col' : 'px-3 py-2',
          )}
        >
          <button
            type="button"
            onClick={layout.toggleCollapse}
            className={cn(
              'flex items-center rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground transition-colors',
              layout.collapsed
                ? 'justify-center h-8 w-8'
                : 'gap-2 flex-1 px-2 py-1.5 text-xs',
            )}
            title={layout.collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {layout.collapsed ? (
              <ChevronsRight className="w-3.5 h-3.5" />
            ) : (
              <>
                <ChevronsLeft className="w-3.5 h-3.5" />
                <span>Collapse</span>
              </>
            )}
          </button>
          <LayoutControl variant="rail" showLabel={!layout.collapsed} />
          <EmailBackgroundControl showLabel={!layout.collapsed} />
        </div>
      )}
    </>
  )
}
